import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import LoadingSpinner from '../../../components/LoadingSpinner'
import GroupLeaderBottomNav from '../../../components/GroupLeaderBottomNav'
import { 
  User,
  Envelope,
  Phone,
  Bell,
  Lock,
  Users,
  HouseLine,
  SignOut,
  Cake, 
  City,
  Mailbox,
  AddressBookTabs,
  CaretRight,
  CalendarBlank,
  Buildings,
  MapPin,
  Warehouse,
  PencilSimple,
  Camera,
  Building,
  MapTrifold,
  Flag
} from '@phosphor-icons/react'
import { 
  requestNotificationPermission, 
  subscribeToPushNotifications, 
  unsubscribeFromPushNotifications,
  checkNotificationStatus 
} from '../../../utils/pushNotifications'

export default function GroupLeaderProfile() {
  const navigate = useNavigate()
  const [userData, setUserData] = useState(null)
  const [orgData, setOrgData] = useState(null)
  const [hierarchyInfo, setHierarchyInfo] = useState(null)
  const [stats, setStats] = useState({ rate: 0, attended: 0, total: 0 })
  const [pushNotifications, setPushNotifications] = useState(false)
  const [emailNotifications, setEmailNotifications] = useState(true)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [enablingNotifications, setEnablingNotifications] = useState(false)

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      
      const { data: userDataResult } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()
      
      setUserData(userDataResult)

      // Check notification status
      const notifStatus = await checkNotificationStatus(supabase, user.id)
      setPushNotifications(notifStatus)

      // Get organization data
      const { data: orgDataResult } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', userDataResult.org_id)
        .single()

      setOrgData(orgDataResult)

      // Get hierarchy info (group they lead) - GROUP LEADER SPECIFIC LOGIC
      try {
        const { data: groupMembership } = await supabase
          .from('group_members')
          .select('group_id')
          .eq('user_id', user.id)
          .single()

        // Verify leadership level
        const { data: leaderCheck } = await supabase
          .from('users')
          .select('leadership_level')
          .eq('id', user.id)
          .single()

        if (groupMembership && leaderCheck?.leadership_level === 'group') {
          const { data: groupInfo } = await supabase
            .from('groups')
            .select('id, name, depth, parent_id')
            .eq('id', groupMembership.group_id)
            .single()

          if (groupInfo && groupInfo.depth === 2) {
            // Get parent team
            const { data: parentTeam } = await supabase
              .from('groups')
              .select('id, name')
              .eq('id', groupInfo.parent_id)
              .single()

            setHierarchyInfo({
              team: parentTeam,
              group: groupInfo
            })
          }
        }
      } catch (error) {
        console.log('Error loading hierarchy:', error)
      }

      // Get attendance stats
      const { data: attendanceRecords } = await supabase
        .from('attendance_records')
        .select('*')
        .eq('user_id', user.id)

      const { data: allEvents } = await supabase
        .from('events')
        .select('id')
        .eq('org_id', userDataResult.org_id)
        .lte('start_date', new Date().toISOString().split('T')[0])

      const attended = attendanceRecords?.length || 0
      const total = allEvents?.length || 0
      const rate = total > 0 ? Math.round((attended / total) * 100) : 0

      setStats({ rate, attended, total })

    } catch (error) {
      console.error('Load profile error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  const handleImageChange = async (e) => {
    try {
      const file = e.target.files?.[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB')
        return
      }
      setUploading(true)
      const { data: { user } } = await supabase.auth.getUser()
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}-${Date.now()}.${fileExt}`
      const filePath = `avatars/${fileName}`
      const { error: uploadError } = await supabase.storage
        .from('user-uploads')
        .upload(filePath, file)
      if (uploadError) throw uploadError
      const { data: { publicUrl } } = supabase.storage
        .from('user-uploads')
        .getPublicUrl(filePath)
      const { error: updateError } = await supabase
        .from('users')
        .update({ profile_photo_url: publicUrl })
        .eq('id', user.id)
      if (updateError) throw updateError
      setUserData(prev => ({ ...prev, profile_photo_url: publicUrl }))
    } catch (error) {
      console.error('Upload error:', error)
      if (error.message?.includes('profile_photo_url')) {
        alert('Database not configured. Please add profile_photo_url column to users table.')
      } else {
        alert('Failed to upload image. Please try again.')
      }
    } finally {
      setUploading(false)
    }
  }

  const handleTogglePushNotifications = async () => {
    if (enablingNotifications) return

    setEnablingNotifications(true)
    try {
      const { data: { user } } = await supabase.auth.getUser()

      if (!pushNotifications) {
        // Enable notifications
        const granted = await requestNotificationPermission()
        if (granted) {
          await subscribeToPushNotifications(supabase, user.id)
          setPushNotifications(true)
          alert('Push notifications enabled!')
        } else {
          alert('Permission denied. Enable notifications in your browser settings.')
        }
      } else {
        // Disable notifications
        await unsubscribeFromPushNotifications(supabase, user.id)
        setPushNotifications(false)
        alert('Push notifications disabled')
      }
    } catch (error) {
      console.error('Notification toggle error:', error)
      alert('Failed to update notification settings: ' + error.message)
    } finally {
      setEnablingNotifications(false)
    }
  }

  const getRoleBadgeInfo = () => {
    if (!userData) return { text: 'No department yet', iconColor: 'text-[#64748B]', bgColor: 'bg-[#F8FAFC]' }
    
    // Group Leader (Head) - THIS IS THE ONLY ROLE FOR THIS PAGE
    if (userData.leadership_level === 'group' && hierarchyInfo?.group) {
      return {
        text: `Head - ${hierarchyInfo.group.name}`,
        iconColor: 'text-[#F26D35]',
        bgColor: 'bg-[#FEF0EB]'
      }
    }
    
    return {
      text: 'No department yet',
      iconColor: 'text-[#64748B]',
      bgColor: 'bg-[#F8FAFC]'
    }
  }

  const getRoleDisplay = () => {
    if (!userData) return 'Group Leader'
    
    if (userData.leadership_level === 'group' && hierarchyInfo?.group) {
      return `Head - ${hierarchyInfo.group.name}`
    }
    
    return 'Group Leader'
  }

  const getJoinedDate = () => {
    if (!userData?.created_at) return 'Recently'
    return new Date(userData.created_at).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  if (loading) {
    return <LoadingSpinner message="Loading profile..." />
  }

  const roleBadge = getRoleBadgeInfo()

  return (
    <>
      <div className="bg-white min-h-screen pb-20">
        {/* Blue Header */}
        <div 
          className="bg-[#2248B2] pt-10 pb-8 px-6 relative overflow-hidden"
          style={{
            backgroundImage: 'url(/Line3.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Pattern overlay for better contrast */}
          <div className="absolute inset-0 bg-[#3167fe]/20"></div>

          {/* Content - above pattern */}
          <div className="relative z-10">
            {/* Top row: Title and Bell */}
            <div className="flex items-center justify-between mb-8">
              <h1 className="text-2xl font-semibold text-white">My profile</h1>
              <button className="w-10 h-10 flex items-center justify-center bg-white rounded-full">
                <Bell size={24} weight="fill" className="text-[#64748B]" />
              </button>
            </div>

            {/* Profile Info */}
            <div className="flex items-start gap-4">
              {/* Profile Photo with Camera Icon */}
              <div className="relative flex-shrink-0">
                <label 
                  htmlFor="profile-photo-upload"
                  className="cursor-pointer block"
                >
                  {userData?.profile_photo_url ? (
                    <img 
                      src={userData.profile_photo_url}
                      alt={userData.name}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-4 border-white/20">
                      <User size={40} weight="fill" className="text-white" />
                    </div>
                  )}
                  
                  {/* Camera Icon Badge */}
                  <div className="absolute bottom-0 right-0 bg-[#3167fe] border-4 border-white rounded-full p-1">
                    {uploading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <Camera size={16} weight="fill" className="text-white" />
                    )}
                  </div>
                </label>
                <input
                  id="profile-photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                  disabled={uploading}
                />
              </div>

              {/* Name, Email, and Badge */}
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-semibold text-white mb-0">{userData?.name}</h2>
                <p className="text-base text-white/80 mb-3">{userData?.email}</p>
                
                {/* Department Badge */}
                <div className={`inline-flex items-center gap-2 ${roleBadge.bgColor} rounded-full px-3 py-1.5`}>
                  <Warehouse size={20} weight="fill" className={roleBadge.iconColor} />
                  <span className="text-sm font-normal text-slate-700">{roleBadge.text}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-4">
          {/* Personal & Address Information Card */}
          <div className="bg-white rounded-[20px] border border-slate-200 overflow-hidden">
            <div className="px-5 py-4">
              <h2 className="text-xl font-semibold text-[#475569] mb-4">Personal & Address Information</h2>
              
              <div className="space-y-3">
                {/* Phone Number */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <Phone size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">Phone Number</span>
                  </div>
                  <span className="text-sm text-[#1E293B] text-right mb-4">{userData?.phone || '-'}</span>
                </div>

                {/* Date of Birth */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <Cake size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">Date of Birth</span>
                  </div>
                  <span className="text-sm text-[#1E293B] text-right mb-4">{userData?.date_of_birth || '-'}</span>
                </div>

                {/* House Address */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                   <HouseLine size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">House Address</span>
                  </div>
                  <span className="text-sm text-[#1E293B] text-right max-w-[200px] mb-4">{userData?.address || '-'}</span>
                </div>

                 {/* City */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                   <City size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">City</span>
                  </div>
                  <span className="text-sm text-[#1E293B] text-right mb-4">{userData?.city || '-'}</span>
                </div>

                    {/* State/Province  */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                   <MapTrifold size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">State/Province</span>
                  </div>
                  <span className="text-sm text-[#1E293B] text-right mb-4">{userData?.state || '-'}</span>
                </div>

                {/* Country */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <Flag size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">Country</span>
                  </div>
                  <span className="text-sm text-[#1E293B] text-right mb-4">{userData?.country || '-'}</span>
                </div>
                
                    {/* Post Code */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <Mailbox size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">Post Code</span>
                  </div>
                  <span className="text-sm text-[#1E293B] text-right mb-4">{userData?.post_code || '-'}</span>
                </div>           
              </div>
            </div>
          </div>

          {/* My Organisation Card */}
          <div className="bg-white rounded-[20px] border border-slate-200 overflow-hidden">
            <div className="px-5 py-4">
              <h2 className="text-xl font-semibold text-[#475569] mb-4">My Organisation</h2>
              
              <div className="space-y-3">
                {/* Organisation */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <Building size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">Organisation</span>
                  </div>
                   <span className="text-sm text-[#1E293B] text-right max-w-[200px] mb-4">{orgData?.name || '-'}</span>
                </div>
                

                   {/* My Role */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <AddressBookTabs size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">My Role</span>
                  </div>
                  <span className="text-sm text-[#1E293B] text-right max-w-[200px] mb-4">{getRoleDisplay()}</span>
                </div>
                
                   {/* Date joined */}
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <CalendarBlank size={20} weight="regular" className="text-[#475569]" />
                    <span className="text-sm text-[#475569]">Date Joined</span>
                  </div>
                  <span className="text-sm text-[#1E293B] text-right mb-4">{getJoinedDate()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Account Settings Card */}
          <div className="bg-white rounded-[20px] border border-slate-200 overflow-hidden">
            <div className="px-5 py-4">
              <h2 className="text-xl font-semibold text-[#475569] mb-4">Account Settings</h2>
              
              <div className="space-y-3">
                {/* Update Information */}
                <button 
                  onClick={() => navigate('/group-leader/profile/update-information')}
                  className="flex items-center justify-between w-full"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <User size={20} weight="regular" className="text-[#1E293B]" />
                    <span className="text-sm text-[#475569]">Update Information</span>
                  </div>
                  <CaretRight size={16} weight="regular" className="text-[#1E293B] mb-4" />
                </button>

                {/* Update Password */}
                <button 
                  onClick={() => navigate('/group-leader/profile/update-password')}
                  className="flex items-center justify-between w-full"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Lock size={20} weight="regular" className="text-slate-600" />
                    <span className="text-sm text-[#475569]">Update Password</span>
                  </div>
                  <CaretRight size={16} weight="regular" className="text-[#1E293B] mb-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Preferences Card */}
          <div className="bg-white rounded-[20px] border border-slate-200 overflow-hidden">
            <div className="px-5 py-4">
              <h2 className="text-xl font-semibold text-[#475569] mb-4">Preferences</h2>
              
              <div className="space-y-3">
                {/* Push Notifications */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <Bell size={20} weight="regular" className="text-slate-600" />
                    <span className="text-sm text-[#475569]">Push Notifications</span>
                  </div>
                  <button
                    onClick={handleTogglePushNotifications}
                    disabled={enablingNotifications}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      pushNotifications ? 'bg-[#3167fe]' : 'bg-slate-300'
                    } ${enablingNotifications ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        pushNotifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Email Notifications */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 mb-4">
                    <Envelope size={20} weight="regular" className="text-slate-600" />
                    <span className="text-sm text-[#475569]">Email Notifications</span>
                  </div>
                  <button
                    onClick={() => setEmailNotifications(!emailNotifications)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      emailNotifications ? 'bg-[#3167fe]' : 'bg-slate-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        emailNotifications ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <button 
            onClick={handleLogout}
            className="bg-white rounded-[20px] border border-red-200 px-6 py-4 w-full hover:bg-red-50 transition-colors"
          >
            <div className="flex items-center justify-center gap-3">
              <SignOut size={24} weight="regular" className="text-red-600" />
              <span className="text-red-600 font-semibold">Logout</span>
            </div>
          </button>
        </div>
      </div>
      
      <GroupLeaderBottomNav />
    </>
  )
}